import * as React from "react";
import { Eye, EyeOff } from "@semo/icons";
import { Input, type InputProps } from "../primitives/input";
import { cn } from "../utils/cn";
import { convertHangulToEnglish } from "@semo/shared-utils";

export interface PasswordInputProps extends Omit<InputProps, "type"> {
  /**
   * Whether to automatically convert Korean characters to English
   * @default true
   */
  autoConvertKorean?: boolean;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, autoConvertKorean = true, onChange, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (autoConvertKorean) {
        const originalValue = e.target.value;
        const convertedValue = convertHangulToEnglish(originalValue);

        // Only update if conversion happened to avoid cursor jumping if possible
        // (Though simple replacement is usually fine for password fields where cursor position matters less)
        if (originalValue !== convertedValue) {
          e.target.value = convertedValue;
        }
      }

      // Call parent onChange (essential for react-hook-form)
      onChange?.(e);
    };

    return (
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          className={cn("pr-10", className)}
          ref={ref}
          onChange={handleChange}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground transition-colors"
          tabIndex={-1} // Prevent tabbing to this button while navigating form
          aria-label={showPassword ? "입력 내용 숨기기" : "입력 내용 보기"}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);

PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
